import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    useTheme,
    LinearProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getFinalAssessment } from "../api/courseAPI";
import { Fade } from "@mui/material";
import { submitFinalAssessment } from "../api/finalAssessment";

export default function FinalAssessmentStart() {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();

    const [questions, setQuestions] = useState([])
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(false);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [visited, setVisited] = useState({});
    const [review, setReview] = useState({});

    const TOTAL_TIME_MINUTES = 30;
    const [timeLeft, setTimeLeft] = useState(TOTAL_TIME_MINUTES * 60);

    // Fetch Questions
    useEffect(() => {
        const fetchAssessment = async () => {
            try {
                setLoading(true);
                const res = await getFinalAssessment(courseId);

                if (res.success && res.data?.length > 0) {
                    setQuestions(res.data[0].questions || []);
                }
            } catch (err) {
                console.error("Failed to fetch questions", err);
            } finally {
                setLoading(false);
            }
        };

        fetchAssessment();
    }, [courseId]);

    // Timer
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleSubmit(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    const handleOptionChange = (questionIndex, value) => {
        setAnswers((prev) => ({
            ...prev,
            [questionIndex]: value,
        }));
    };

    

    const handleSubmit = async (auto = false) => {
        if (!auto) {
            const confirmSubmit = window.confirm(
                "Are you sure you want to submit?"
            );
            if (!confirmSubmit) return;
        }

        try {
            const res = await submitFinalAssessment(courseId, answers);

            if (document.fullscreenElement) {
                await document.exitFullscreen();
            }

            alert(
                `Score: ${res.data.score}/${res.data.totalQuestions}\n` +
                `Percentage: ${res.data.percentage.toFixed(2)}%\n` +
                (res.data.passed ? "PASSED 🎉" : "FAILED ❌")
            );

            navigate(`/course/${courseId}`);

        } catch (err) {
            console.error("Submission failed", err);
        }
    };

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? "0" : ""}${s}`;
    };

    const answeredCount = Object.keys(answers).length;
    const progressPercent =
        questions.length > 0
            ? (answeredCount / questions.length) * 100
            : 0;

    useEffect(() => {
        const enterFullscreen = async () => {
            if (document.documentElement.requestFullscreen) {
                await document.documentElement.requestFullscreen();
            }
        };

        enterFullscreen();
    }, []);

    useEffect(() => {
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement) {
                alert("You exited fullscreen mode. Please return to fullscreen to continue the assessment.");
                // Optional strict mode:
                // handleSubmit(true);
            }
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);

        return () =>
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = "Are you sure you want to leave? Your assessment will be submitted.";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () =>
            window.removeEventListener("beforeunload", handleBeforeUnload);
    }, []);

    useEffect(() => {
        window.history.pushState(null, "", window.location.href);

        const handlePopState = () => {
            window.history.pushState(null, "", window.location.href);
            alert("Back navigation is disabled during the assessment.");
        };

        window.addEventListener("popstate", handlePopState);

        return () => window.removeEventListener("popstate", handlePopState);
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                alert("Tab switching is not allowed during the assessment.");
                // Optional strict mode:
                // handleSubmit(true);
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () =>
            document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, []);



    return (
        <Box
            sx={{
                display: "flex",
                minHeight: "100vh",
                backgroundColor: theme.palette.background.default,
            }}
        >
            {/* ================= LEFT SIDEBAR ================= */}
            {/* ================= LEFT SIDEBAR ================= */}
            <Box
                sx={{
                    width: 320,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Header */}
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={600}>
                        Assessment Navigator
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Track your progress
                    </Typography>
                </Box>

                {/* Progress Bar */}
                <Box sx={{ px: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                        Completion
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={progressPercent}
                        sx={{ mt: 1, height: 8, borderRadius: 5 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                        {answeredCount} of {questions.length} answered
                    </Typography>
                </Box>

                {/* Question Grid */}
                <Box
                    sx={{
                        p: 3,
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: 1.5,
                        flexGrow: 1,
                        overflowY: "auto",
                    }}
                >
                    {questions.map((_, index) => {
                        const isCurrent = currentIndex === index;
                        const isAnswered = !!answers[index];
                        const isReview = !!review[index];

                        let bg = theme.palette.grey[300];
                        let color = "#000";

                        if (isCurrent) {
                            bg = theme.palette.primary.main;
                            color = "#fff";
                        } else if (isAnswered && isReview) {
                            bg = theme.palette.info.main; // Answered + Review
                            color = "#fff";
                        } else if (isAnswered) {
                            bg = theme.palette.success.main;
                            color = "#fff";
                        } else if (isReview) {
                            bg = theme.palette.warning.main;
                            color = "#fff";
                        }

                        return (
                            <Box
                                key={index}
                                onClick={() => setCurrentIndex(index)}
                                sx={{
                                    height: 42,
                                    width: 42,
                                    borderRadius: "50%",
                                    backgroundColor: bg,
                                    color: color,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 14,
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    transition: "0.2s",
                                    boxShadow: isCurrent ? 3 : 0,
                                    "&:hover": {
                                        transform: "scale(1.08)",
                                    },
                                }}
                            >
                                {index + 1}
                            </Box>
                        );
                    })}
                </Box>

                {/* Submit */}
                <Box sx={{ p: 3 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        onClick={() => handleSubmit(false)}
                    >
                        Submit Assessment
                    </Button>
                </Box>
            </Box>

            {/* ================= MAIN QUESTION AREA ================= */}
            <Box
                sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        p: 2,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        display: "flex",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography variant="h6">
                        Question {questions.length > 0 ? currentIndex + 1 : 0} of {questions.length}
                    </Typography>

                    <Typography color="error" fontWeight={600}>
                        {formatTime(timeLeft)}
                    </Typography>
                </Box>

                {loading && (
                    <Box p={4}>
                        <Typography>Loading Questions...</Typography>
                    </Box>
                )}

                {/* Question Card */}
                {questions.length > 0 && questions[currentIndex] && (
                    <Fade in key={currentIndex} timeout={300}>
                        <Card sx={{ borderRadius: 2, m: 4 }}>
                            <CardContent>
                                <Typography fontWeight={600} mb={3}>
                                    {questions[currentIndex]?.QUESTION}
                                </Typography>

                                <RadioGroup
                                    value={answers[currentIndex] || ""}
                                    onChange={(e) =>
                                        handleOptionChange(currentIndex, e.target.value)
                                    }
                                >
                                    {["OPTION A", "OPTION B", "OPTION C", "OPTION D"].map(
                                        (optKey) =>
                                            questions[currentIndex]?.[optKey] && (
                                                <FormControlLabel
                                                    key={optKey}
                                                    value={optKey}
                                                    control={<Radio />}
                                                    label={questions[currentIndex][optKey]}
                                                    sx={{
                                                        mb: 1,
                                                        p: 1,
                                                        borderRadius: 1,
                                                        "&:hover": {
                                                            backgroundColor: theme.palette.action.hover,
                                                        },
                                                    }}
                                                />
                                            )
                                    )}
                                </RadioGroup>
                            </CardContent>
                        </Card>
                    </Fade>
                )}

                {/* Navigation Buttons */}
                <Box
                    sx={{
                        p: 2,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                    }}
                >
                    <Button
                        variant="outlined"
                        disabled={currentIndex === 0}
                        onClick={() => setCurrentIndex((prev) => prev - 1)}
                    >
                        Previous
                    </Button>

                    <Button
                        variant="outlined"
                        color="warning"
                        onClick={() =>
                            setReview((prev) => ({
                                ...prev,
                                [currentIndex]: !prev[currentIndex],
                            }))
                        }
                    >
                        {review[currentIndex] ? "Unmark Review" : "Mark for Review"}
                    </Button>

                    <Button
                        variant="contained"
                        disabled={currentIndex === questions.length - 1}
                        onClick={() => setCurrentIndex((prev) => prev + 1)}
                    >
                        Next
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}